<template>
  <!-- ── FT-1398: THE NIGHT LAB'S STRIP ─────────────────────────────────────
       The controls for golem/nightLab.js — the model, the guards, the
       snapshot/restore and every store commit live THERE; this file is only
       the compact strip (the chair/ghost-lab shell: left-docked plate, the
       same bone-on-dark voice), plus the door out.

       UNLIKE THE DEV COLUMN this is opened from the golem mark's own menu
       (AccountMenu's Night lab row — owner-only since FT-1403: the row and
       nightLab.enter() both gate on canUseLab, the platform's superuser
       tier or the `devLabs` browser flag) and only ever exists while the
       lab is running — the root v-if reads the module's observable, so
       App.vue mounts this unconditionally and the closed lab costs one
       comment node. -->
  <div id="night-lab" v-if="lab.open">
    <div class="nl-rows">
      <div class="nl-head">
        Night lab
        <span
          class="nl-live"
          title="Client-local preview — no town, no relay; nothing here can reach a live game"
          >offline</span
        >
        <button
          type="button"
          class="nl-exit"
          title="Close the lab and restore the table exactly as it was"
          @click="close"
        >
          Exit
        </button>
      </div>
      <!-- THE ROLE — grouped by script, plus the travellers who wake. Only
           characters with a night act are listed; a role without a bespoke
           dress previews its real fallback, which is the honest answer. -->
      <select
        class="nl-role"
        :value="lab.roleId"
        title="Whose night to preview — grouped by script; travellers with night acts at the bottom"
        @change="pickRole($event.target.value)"
      >
        <optgroup v-for="g in groups" :key="g.id" :label="g.name">
          <option v-for="r in g.roles" :key="r.id" :value="r.id">
            {{ r.name }}
          </option>
        </optgroup>
      </select>
      <!-- THE STATE — the role's own grammar, one chip per beat. Choosers
           walk invitation → staged → sealed → ST sent → reopened; told
           roles walk before → telling → settled → residue. -->
      <div class="nl-head nl-sub">State</div>
      <div class="nl-chips">
        <button
          type="button"
          v-for="s in grammar"
          :key="s"
          class="nl-chip"
          :class="{ on: lab.stateId === s }"
          :title="stateHint(s)"
          @click="pickState(s)"
        >
          {{ stateLabel(s) }}
        </button>
      </div>
      <!-- THE EYES — whose screen this is. The bystander sees nothing (that
           silence is the FT-1384/1385 privacy fact, shown rather than
           claimed); the storyteller sees the sheet, never the dress. -->
      <div class="nl-head nl-sub">Seen by</div>
      <div class="nl-chips">
        <button
          type="button"
          v-for="p in perspectives"
          :key="p.id"
          class="nl-chip"
          :class="{ on: lab.perspective === p.id }"
          :title="perspectiveHint(p.id)"
          @click="pickPerspective(p.id)"
        >
          {{ p.label }}
        </button>
      </div>
      <button
        type="button"
        class="nl-replay"
        title="Re-fire this state's arrival animation (steps back one beat and forward again)"
        @click="fireReplay"
      >
        Replay
      </button>
      <div class="nl-hint" v-if="isChooser && lab.perspective === 'acting'">
        tap a seat to place or move the pick
      </div>
      <!-- ── FT-1403 phase 2: THE COPY PANE ────────────────────────────────
           Every night sentence this role can speak, as a box. Typing rewrites
           the on-screen sentence on the keystroke — the edit goes through the
           SAME sanitizing door a script lands through (golem/nightLab's
           setCopyDraft → registerNightCopy), so the lab can never show text a
           real script could not carry.

           FT-1408 (user): the box HOLDS its sentence. Where the shipped
           default is an editable template (Sheet line, Ask, Sealed) the box
           opens on that text so a word can be changed rather than the whole
           line retyped; a box left equal to the seed is no override (the ↺
           only appears once one stands). A box the typist EMPTIES stays
           empty while it keeps focus, so select-all-delete-type writes a
           fresh sentence and not "seed + fresh". Telling/Settled have no
           template and keep the ghost.

           FT-1410: a role whose SHEET ROW speaks both halves also owns that
           row's two labels — Seat label and Told label, the words before the
           pickers and before the answer ("Player:" … "Role:" on the
           first-night info rows). They seed like the other templates, take no
           placeholders, and ride the same Save / Copy JSON as every sentence
           on this pane; the rows themselves come from copyKeysFor. -->
      <div class="nl-head nl-sub">Copy</div>
      <div class="nl-copy-row" v-for="row in copyRows" :key="row.key">
        <div class="nl-copy-lab">
          <span>{{ row.label }}</span>
          <button
            type="button"
            class="nl-reset"
            v-if="row.value"
            title="Back to the shipped default"
            @click="resetRow(row.key)"
          >
            ↺
          </button>
        </div>
        <textarea
          class="nl-copy-box"
          rows="1"
          :maxlength="copyMax"
          :placeholder="row.placeholder"
          :value="shownFor(row)"
          :title="
            row.seed
              ? 'The shipped default — edit it in place; ↺ brings it back'
              : ''
          "
          @input="onCopyInput(row.key, $event)"
          @focus="onCopyFocus(row.key, $event)"
          @blur="onCopyBlur($event)"
        ></textarea>
      </div>
      <div class="nl-hint">{{ copyHint }}</div>
      <div class="nl-copy-acts">
        <button
          type="button"
          class="nl-chip"
          v-if="canSave"
          title="Write these sentences onto the loaded custom script — survives Exit"
          @click="saveCopy"
        >
          Save to script
        </button>
        <button
          type="button"
          class="nl-chip"
          title="Put the whole nightCopy section on the clipboard, ready to paste into a script's _meta"
          @click="copyJson"
        >
          {{ copied ? "Copied" : "Copy JSON" }}
        </button>
        <button
          type="button"
          class="nl-chip"
          title="Every sentence of THIS role back to its shipped default"
          @click="resetRole"
        >
          Reset all
        </button>
      </div>
      <div class="nl-hint" v-if="!canSave">
        Official script — export the JSON or load a custom script
      </div>
      <div class="nl-hint">unsaved edits are lost on Exit</div>
    </div>
  </div>
</template>

<script>
import {
  nightLab,
  roleGroups,
  grammarOf,
  applyState,
  setRole,
  setPerspective,
  replay,
  exit,
  STATE_LABELS,
  PERSPECTIVES,
  // FT-1403 phase 2: the copy editor's model — the draft, the merge, the
  // shipped defaults, the one save. This file only renders it.
  copyDraft,
  copyKeysFor,
  copyDefaultFor,
  copySeedFor,
  copyValueFor,
  copyPlaceholderHint,
  copyJsonText,
  setCopyDraft,
  clearCopyDraft,
  clearRoleCopy,
  canSaveCopy,
  saveCopyToScript,
  COPY_LABELS,
  COPY_MAX,
} from "../golem/nightLab";
import { TOLD_ROLES } from "../golem/toldInfo";

const STATE_HINTS = {
  invite: "The night is asking — every coin breathes the invitation",
  staged: "A pick is placed but NOT sent — it can still move freely",
  sealed: "Confirm pressed — the choice is sealed on this side",
  "st-sent": "The storyteller answered and sent — the answer arrives",
  reopened: "The storyteller re-asked — the ring unlocks again",
  before: "Asleep — nothing has been delivered yet",
  telling: "The send lands — the information arrives in the role's own dress",
  settled: "The bright telling eases into the quiet residue",
  residue: "Day breaks — knowledge persists on the coins",
};

const PERSPECTIVE_HINTS = {
  acting: "The acting player's own screen",
  bystander: "Any other seat — sees nothing of this night action",
  storyteller: "The host's screen — the sheet and the record, never the dress",
};

export default {
  name: "NightLab",
  data() {
    return {
      lab: nightLab,
      groups: roleGroups(),
      perspectives: PERSPECTIVES,
      // the copy editor's working edits — held here so the pane's computeds
      // take a dependency on it (the module owns every write)
      draft: copyDraft,
      copyMax: COPY_MAX,
      copied: false,
      // FT-1408: the box being typed in, and whether the typist emptied it —
      // an emptied box stays empty until the cursor leaves
      focusKey: "",
      cleared: false,
    };
  },
  computed: {
    grammar() {
      return this.lab.roleId ? grammarOf(this.lab.roleId) : [];
    },
    isChooser() {
      return (
        this.lab.roleId &&
        !TOLD_ROLES[this.lab.roleId] &&
        this.grammar.indexOf("staged") !== -1
      );
    },
    /** One row per sentence THIS role can actually speak. `seed` is the
     *  shipped template the box opens on (FT-1408); the placeholder is the
     *  shipped default for the keys that have no template — both resolved
     *  through the app's own fallbacks, never a second copy of a string. */
    copyRows() {
      const id = this.lab.roleId;
      if (!id) return [];
      // the draft read that makes this list repaint as boxes are typed in
      const drafted = this.draft.map[id];
      return copyKeysFor(id).map((key) => {
        const seed = copySeedFor(id, key);
        return {
          key,
          label: COPY_LABELS[key] || key,
          seed,
          placeholder: seed
            ? ""
            : copyDefaultFor(id, key) || "(nothing shipped — write one)",
          value:
            (drafted && drafted[key]) || copyValueFor(this.$store, id, key),
        };
      });
    },
    copyHint() {
      return copyPlaceholderHint(this.lab.roleId);
    },
    canSave() {
      return canSaveCopy(this.$store);
    },
  },
  mounted() {
    this.growAll();
  },
  updated() {
    this.growAll();
  },
  beforeDestroy() {
    this.markFocus("");
  },
  methods: {
    stateLabel(s) {
      return STATE_LABELS[s] || s;
    },
    stateHint(s) {
      return STATE_HINTS[s] || "";
    },
    perspectiveHint(p) {
      return PERSPECTIVE_HINTS[p] || "";
    },
    pickRole(id) {
      setRole(this.$store, id);
    },
    pickState(s) {
      applyState(this.$store, s);
    },
    pickPerspective(p) {
      setPerspective(this.$store, p);
    },
    fireReplay() {
      replay(this.$store);
    },
    close() {
      exit(this.$store);
    },
    // ── the copy pane ───────────────────────────────────────────────────
    /** What the box shows: the override where one stands, else the seed —
     *  except a box the typist has just EMPTIED, which stays empty while it
     *  keeps focus so the seed does not refill under the cursor (FT-1408). */
    shownFor(row) {
      if (row.value) return row.value;
      return this.focusKey === row.key && this.cleared ? "" : row.seed;
    },
    onCopyInput(key, ev) {
      this.cleared = !ev.target.value;
      setCopyDraft(this.$store, this.lab.roleId, key, ev.target.value);
      this.grow(ev.target);
    },
    onCopyFocus(key) {
      this.focusKey = key;
      this.cleared = false;
      this.markFocus(key);
    },
    onCopyBlur(ev) {
      this.focusKey = "";
      this.cleared = false;
      this.markFocus("");
      this.$nextTick(() => this.grow(ev.target));
    },
    resetRow(key) {
      clearCopyDraft(this.$store, this.lab.roleId, key);
    },
    resetRole() {
      clearRoleCopy(this.$store, this.lab.roleId);
    },
    saveCopy() {
      saveCopyToScript(this.$store);
    },
    copyJson() {
      const text = copyJsonText(this.$store);
      const flash = () => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 1500);
      };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(flash, () => {
            this.legacyCopy(text, flash);
          });
          return;
        }
      } catch (e) {
        // no clipboard API (an insecure origin, an old browser): fall through
      }
      this.legacyCopy(text, flash);
    },
    /** The pre-clipboard-API path, kept because the lab is often opened on
     *  a plain http:// dev origin where navigator.clipboard is absent. */
    legacyCopy(text, done) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch (e) {
        // nothing else to try — the flash below still says what was offered
      }
      document.body.removeChild(ta);
      done();
    },
    /** THE RIDER: while a box has focus, the sentence it writes wears a soft
     *  outline on screen. One body attribute, matched by the unscoped rule at
     *  the foot of this file against the `data-night-copy` marks the render
     *  paths carry. */
    markFocus(key) {
      try {
        if (key) document.body.setAttribute("data-nl-copy-focus", key);
        else document.body.removeAttribute("data-nl-copy-focus");
      } catch (e) {
        // no document (tests): the outline is decoration, never a guard
      }
    },
    /** 1–3 lines, grown to fit. */
    grow(el) {
      if (!el) return;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 58) + "px";
    },
    growAll() {
      if (!this.$el || !this.$el.querySelectorAll) return;
      this.$el.querySelectorAll(".nl-copy-box").forEach((el) => this.grow(el));
    },
  },
};
</script>

<style scoped lang="scss">
// The dev column's shell voice (GhostLab et al.) — left-docked plate, bone
// text on near-black, plum accents — below the FT-1258 ladder's last rung so
// a devLabs browser can run both.
#night-lab {
  position: fixed;
  top: 306px;
  left: 0;
  z-index: 60;
  display: flex;
  align-items: flex-start;
  font-size: 13px;

  .nl-rows {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px;
    width: 168px;
    background: rgba(8, 6, 10, 0.92);
    border: 1px solid rgba(var(--reader-rgb), 0.45);
    border-left: none;
    border-radius: 0 8px 8px 0;
    max-height: calc(100vh - 330px);
    overflow-y: auto;
  }
  .nl-head {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 10px;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: #b6a8c8;
    opacity: 0.85;
    padding-bottom: 3px;
    border-bottom: 1px solid rgba(var(--reader-rgb), 0.28);
  }
  .nl-sub {
    margin-top: 4px;
  }
  .nl-live {
    font-size: 9px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #0d0a12;
    background: rgb(var(--reader-rgb));
    border-radius: 3px;
    padding: 1px 4px;
    cursor: help;
  }
  .nl-exit {
    margin-left: auto;
    font-family: inherit;
    font-size: 10px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #d8cdb4;
    background: rgba(20, 16, 22, 0.9);
    border: 1px solid rgba(var(--reader-rgb), 0.4);
    border-radius: 5px;
    padding: 1px 6px;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      border-color: rgba(var(--reader-rgb), 0.75);
      outline: none;
    }
  }
  .nl-role {
    font-family: inherit;
    font-size: 12px;
    color: #d8cdb4;
    background: rgba(20, 16, 22, 0.9);
    border: 1px solid rgba(var(--reader-rgb), 0.4);
    border-radius: 5px;
    padding: 2px 4px;
    max-width: 100%;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      border-color: rgba(var(--reader-rgb), 0.75);
      outline: none;
    }
  }
  .nl-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
  }
  .nl-chip {
    font-family: inherit;
    font-size: 11px;
    color: #cdc2e2;
    background: rgba(20, 16, 22, 0.9);
    border: 1px solid rgba(var(--reader-rgb), 0.4);
    border-radius: 5px;
    padding: 2px 6px;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      border-color: rgba(var(--reader-rgb), 0.75);
      outline: none;
    }
    &.on {
      color: #0d0a12;
      background: rgb(var(--reader-rgb));
      border-color: rgb(var(--reader-rgb));
    }
  }
  .nl-replay {
    font-family: inherit;
    font-size: 11px;
    color: #d8cdb4;
    background: rgba(20, 16, 22, 0.9);
    border: 1px solid rgba(var(--reader-rgb), 0.4);
    border-radius: 5px;
    padding: 2px 6px;
    cursor: pointer;
    align-self: flex-start;
    margin-top: 4px;
    &:hover,
    &:focus-visible {
      border-color: rgba(var(--reader-rgb), 0.75);
      outline: none;
    }
  }
  .nl-hint {
    font-size: 9px;
    letter-spacing: 0.04em;
    color: rgb(var(--reader-rgb));
  }
  // ── FT-1403 phase 2: the copy pane ──────────────────────────────────────
  .nl-copy-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .nl-copy-lab {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 9px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: rgb(var(--reader-rgb));
  }
  .nl-reset {
    margin-left: auto;
    font-family: inherit;
    font-size: 11px;
    line-height: 1;
    color: rgb(var(--reader-rgb));
    background: none;
    border: none;
    padding: 0 2px;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      color: #e6dcff;
      outline: none;
    }
  }
  .nl-copy-box {
    font-family: inherit;
    font-size: 11px;
    line-height: 1.35;
    color: #d8cdb4;
    background: rgba(20, 16, 22, 0.9);
    border: 1px solid rgba(var(--reader-rgb), 0.4);
    border-radius: 5px;
    padding: 3px 5px;
    width: 100%;
    box-sizing: border-box;
    resize: none;
    overflow: hidden;
    &::placeholder {
      color: #6f6482;
    }
    &:hover,
    &:focus-visible,
    &:focus {
      border-color: rgba(var(--reader-rgb), 0.75);
      outline: none;
    }
  }
  .nl-copy-acts {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    margin-top: 2px;
  }
}
</style>

<!-- FT-1403 phase 2, THE RIDER — UNSCOPED on purpose: the outline lands on
     sentences rendered by OTHER components (the face's ask, the telling, the
     storyteller's sheet row), so a scoped rule could never reach them. It is
     keyed off a body attribute this component sets on focus and clears on
     blur and on unmount, and off the `data-night-copy` marks the render paths
     carry — a mark that exists whether or not the lab is running and costs
     nothing when it is not. -->
<style lang="scss">
body[data-nl-copy-focus="invite"] [data-night-copy="invite"],
body[data-nl-copy-focus="sealed"] [data-night-copy="sealed"],
body[data-nl-copy-focus="line"] [data-night-copy="line"],
body[data-nl-copy-focus="telling"] [data-night-copy="telling"],
body[data-nl-copy-focus="settled"] [data-night-copy="settled"] {
  outline: 1px dashed rgba(var(--reader-rgb), 0.85);
  outline-offset: 3px;
  border-radius: 3px;
}
</style>
